import { ReactNode, useCallback, useState } from "react";
import { ScheduleSpecificDateApplyModal } from "./ScheduleSpecificDateApplyModal";
import {
    ScheduleSpecificDateApplyRequest,
    ScheduleSpecificDateApplyResult,
} from "./scheduleSpecificDateApply";
import { useScheduleSpecificDateRequest } from "./useScheduleSpecificDateRequest";

export interface ScheduleSpecificDateApply<
    TRequest extends ScheduleSpecificDateApplyRequest,
> {
    /** Opens the modal; resolves to whether the schedule was applied once it closes. */
    open: (request: TRequest) => Promise<boolean>;
    modal: ReactNode;
}

/**
 * The Specific date step a surface mounts once: the pending request, the
 * modal that collects the date, and the apply that closes it once the
 * schedule lands. The modal stays locked for the whole apply, including the
 * refetch a surface runs after its mutation, so a second Apply or a Cancel
 * cannot slip in while the first apply is still settling.
 */
export function useScheduleSpecificDateApply<
    TRequest extends ScheduleSpecificDateApplyRequest,
>(options: {
    /** Applies the pending request; resolves to whether the schedule was applied. */
    apply: (
        pending: TRequest,
        result: ScheduleSpecificDateApplyResult,
    ) => Promise<boolean>;
    isApplying: boolean;
}): ScheduleSpecificDateApply<TRequest> {
    const { apply, isApplying } = options;
    const specificDate = useScheduleSpecificDateRequest<TRequest>();
    const [isInFlight, setIsInFlight] = useState(false);

    const applyWithDate = useCallback(
        async (result: ScheduleSpecificDateApplyResult) => {
            const pending = specificDate.request;
            if (!pending || isInFlight) {
                return;
            }
            setIsInFlight(true);
            const applyPending = async () => {
                const isApplied = await apply(pending, result);
                if (isApplied) {
                    specificDate.complete(pending);
                }
            };
            await applyPending().finally(() => {
                setIsInFlight(false);
            });
        },
        [apply, isInFlight, specificDate],
    );

    return {
        open: specificDate.open,
        modal: (
            <ScheduleSpecificDateApplyModal
                request={specificDate.request}
                onHide={specificDate.cancel}
                isSubmitting={isApplying || isInFlight}
                onSubmit={(result) => void applyWithDate(result)}
            />
        ),
    };
}
